import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FilterService } from '@app/core/services/container/filter.service';
import { FieldType } from '@app/shared/constants';
import { FilterOutput } from '@app/shared/models/filter.interface';
import { RSQLOperator } from '@app/shared/models/rsql/rsql.operators';
import { DataTableState } from '@components/data/data-table-new/state/state.model';
import { StateService } from '@components/data/data-table-new/state/state.service';
import { combineLatest, Observable } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';
import { Notification } from '../../model/notification.model';
import { NotificationCommandService } from '../../services/notification-command.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationViewerTableData } from './notification-viewer.component.table';

export interface NotificationFilter {
  unread: boolean;
  flagged: boolean;
  important: boolean;
  error: boolean;
}

@Component({
  selector: 'vfs-notification-viewer',
  templateUrl: './notification-viewer.component.html',
  styleUrls: ['./notification-viewer.component.scss']
})
export class NotificationViewerComponent implements OnInit, OnDestroy {
  tableState$: Observable<DataTableState>;
  notifications$: Observable<Notification>;
  all$: Observable<any>;

  data: Notification[] = [];
  total = -1;
  loading: boolean;

  @Output() closeViewer = new EventEmitter<void>();

  constructor(
    protected notificationService: NotificationService,
    public notificationCommandService: NotificationCommandService,
    protected filterService: FilterService,
    public notificationViewerTableData: NotificationViewerTableData,
    @Inject('StateService') protected stateService: StateService
  ) {}

  ngOnInit() {
    const state: DataTableState = this.notificationViewerTableData.getState();
    this.tableState$ = this.notificationViewerTableData.stateSubject$.pipe(startWith(state));
    this.notifications$ = this.notificationService.getLiveNotifications().pipe(startWith(null));
    this.all$ = combineLatest(this.tableState$, this.notifications$).pipe(
      tap(result => this.notificationCommandService.refreshData(result[0], result[1]))
    );
  }

  onClosePanelButtonClick() {
    this.closeViewer.emit();
  }

  onBarChanges(values: NotificationFilter) {
    let state: DataTableState = this.notificationViewerTableData.getState();
    const applyFilters: boolean = Object.keys(values).length > 0;
    if (applyFilters) {
      state.filter.value = {
        unread: this.getFilter('unread', values),
        flagged: this.getFilter('flagged', values),
        important: this.getFilter('important', values),
        error: this.getFilter('error', values)
      };
    } else {
      state.filter.value = {};
    }
    this.notificationCommandService.refreshData(state);
  }

  protected getFilter(key: string, values): FilterOutput {
    let categoryFilters = ['error'];
    if (!categoryFilters.includes(key)) {
      return {
        key: `notificationUsers.${key}`,
        value: values[key],
        valueType: FieldType.BOOLEAN,
        operator: RSQLOperator.EQUAL
      };
    } else {
      return {
        key: 'category',
        value: values[key] ? key.toUpperCase() : null,
        valueType: FieldType.STRING,
        operator: RSQLOperator.EQUAL
      };
    }
  }

  public markAllAsRead(): void {
    this.notificationCommandService.markAllAsRead();
  }

  public markSelectedAsRead(read: boolean): void {
    this.notificationCommandService.markSelectedAsRead(read);
  }

  public markSelectedAsFlagged(flagged: boolean): void {
    this.notificationCommandService.markSelectedAsFlagged(flagged);
  }

  public deleteSelected(): void {
    this.notificationCommandService.deleteSelected();
  }

  public isLoading(): boolean {
    return this.notificationCommandService.isLoading();
  }

  ngOnDestroy(): void {
    const newState = this.notificationViewerTableData.getState();
    newState.filter.value = {};
    this.stateService.update(newState);
    this.notificationCommandService.clear();
  }
}
